import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { edgeFunctionErrorMessage } from "@/lib/edgeFunctionError";
import { signatureSendOutcome } from "@/lib/signatureSend";
import type {
  DocumentField,
  DocumentFieldInput,
  DocumentFieldType,
  ClientSigner,
} from "@/components/admin/documents/types";

export type ContractMode = "richtext" | "pdf_upload";
export type ContractStatus = "draft" | "sent" | "partially_signed" | "signed" | "rejected";

export interface Contract {
  id: string;
  lead_id: string;
  budget_id: string | null;
  mode: ContractMode;
  title: string;
  status: ContractStatus;
  signature_flow: "parallel" | "sequential";
  richtext_content: string | null;
  source_pdf_url: string | null;
  generated_pdf_url: string | null;
  base_document_hash: string | null;
  signed_pdf_url: string | null;
  finalized_at: string | null;
  pending_client_signers: ClientSigner[] | null;
  created_at: string;
  updated_at: string;
}

export type ContractFieldType = DocumentFieldType;
export type ContractField = DocumentField & { contract_id: string };
export type ContractFieldInput = DocumentFieldInput;

export interface ContractSignature {
  id: string;
  contract_id: string;
  signer_name: string;
  signer_email: string;
  order_index: number;
  token: string;
  status: "pending" | "signed" | "rejected";
  signature_type: "drawn" | "typed" | "certificate" | null;
  signature_data: string | null;
  certificate_info: Record<string, unknown> | null;
  rejection_reason: string | null;
  sent_at: string | null;
  signed_at: string | null;
  rejected_at: string | null;
  ip_address: string | null;
  user_agent: string | null;
  viewed_at: string | null;
  viewed_ip: string | null;
  expires_at: string;
  created_at: string;
  updated_at: string;
}

// ─── Contracts ──────────────────────────────────────────────────────────────

export function useContracts(leadId: string | undefined) {
  return useQuery({
    queryKey: ["contracts", leadId],
    queryFn: async () => {
      if (!leadId) return [];
      const { data, error } = await supabase
        .from("contracts" as never)
        .select("*")
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as unknown as Contract[];
    },
    enabled: !!leadId,
  });
}

/** Lightweight list across all leads — feeds deriveStage() in the funnel/sales views. */
export interface ContractStageRow {
  id: string;
  lead_id: string | null;
  budget_id: string | null;
  status: ContractStatus;
}

export function useAllContracts() {
  return useQuery({
    queryKey: ["contracts", "all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contracts" as never)
        .select("id, lead_id, budget_id, status");
      if (error) throw error;
      return data as unknown as ContractStageRow[];
    },
  });
}

export interface CreateContractData {
  lead_id: string;
  budget_id?: string | null;
  mode: ContractMode;
  title: string;
}

export function useCreateContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateContractData) => {
      const { data: contract, error } = await supabase
        .from("contracts" as never)
        .insert(data as never)
        .select()
        .single();
      if (error) throw error;
      return contract as unknown as Contract;
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ["contracts", vars.lead_id] });
      toast.success("Contrato criado!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useUpdateContract(leadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<
        Pick<
          Contract,
          | "title"
          | "richtext_content"
          | "source_pdf_url"
          | "generated_pdf_url"
          | "base_document_hash"
          | "signature_flow"
          | "pending_client_signers"
        >
      >;
    }) => {
      const { data: contract, error } = await supabase
        .from("contracts" as never)
        .update(data as never)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return contract as unknown as Contract;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["contracts", leadId] }),
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

// Switches an existing draft contract to the other authoring mode in place —
// resets mode-specific fields (and drops any placed PDF fields) instead of
// leaving an orphaned draft behind, so there's always a way to "undo" the
// richtext/pdf_upload choice without littering the contract switcher.
export function useSwitchContractMode(leadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, mode }: { id: string; mode: ContractMode }) => {
      const { error: fieldsError } = await supabase
        .from("contract_fields" as never)
        .delete()
        .eq("contract_id", id);
      if (fieldsError) throw fieldsError;

      const { error } = await supabase
        .from("contracts" as never)
        .update({
          mode,
          richtext_content: null,
          source_pdf_url: null,
          generated_pdf_url: null,
          base_document_hash: null,
        } as never)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ["contracts", leadId] });
      queryClient.invalidateQueries({ queryKey: ["contract-fields", vars.id] });
      toast.success("Tipo de contrato alterado!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useDeleteContract(leadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("contracts" as never)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts", leadId] });
      toast.success("Contrato removido!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

// ─── Contract fields (pdf_upload mode) ─────────────────────────────────────

export function useContractFields(contractId: string | undefined) {
  return useQuery({
    queryKey: ["contract-fields", contractId],
    queryFn: async () => {
      if (!contractId) return [];
      const { data, error } = await supabase
        .from("contract_fields" as never)
        .select("*")
        .eq("contract_id", contractId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as unknown as ContractField[];
    },
    enabled: !!contractId,
  });
}

// Replaces the full field set for a contract in one shot — simplest correct
// model for a "draw all your fields, then save" editor.
export function useSaveContractFields(contractId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (fields: ContractFieldInput[]) => {
      const { error: delErr } = await supabase
        .from("contract_fields" as never)
        .delete()
        .eq("contract_id", contractId);
      if (delErr) throw delErr;
      if (fields.length === 0) return;
      const { error: insErr } = await supabase
        .from("contract_fields" as never)
        .insert(fields.map((f) => ({ ...f, contract_id: contractId })) as never);
      if (insErr) throw insErr;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contract-fields", contractId] });
      toast.success("Campos salvos!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

// ─── Contract signatures ────────────────────────────────────────────────────

export function useContractSignatures(contractId: string | undefined) {
  return useQuery({
    queryKey: ["contract-signatures", contractId],
    queryFn: async () => {
      if (!contractId) return [];
      const { data, error } = await supabase
        .from("contract_signatures" as never)
        .select("*")
        .eq("contract_id", contractId)
        .order("order_index", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as unknown as ContractSignature[];
    },
    enabled: !!contractId,
  });
}

export interface SendContractSignatureRequest {
  contractId: string;
  flow: "parallel" | "sequential";
  signers: { name: string; email: string }[];
}

export function useSendContractSignature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SendContractSignatureRequest) => {
      const { data, error } = await supabase.functions.invoke("send-contract-signature", {
        body: payload,
      });
      if (error)
        throw new Error(
          await edgeFunctionErrorMessage(error, "Erro ao enviar solicitação de assinatura"),
        );
      return data;
    },
    onSuccess: (data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["contract-signatures", vars.contractId] });
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      const outcome = signatureSendOutcome(data);
      if (outcome.ok) toast.success(outcome.message);
      else toast.warning(outcome.message);
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

// Reprocessa a finalização quando todos assinaram mas signed_pdf_url ficou
// nulo (finalize-contract falhou silenciosamente na primeira tentativa).
// finalize-contract é idempotente: se já finalizado, retorna sem refazer.
export function useReprocessContractPdf() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (contractId: string) => {
      const { data, error } = await supabase.functions.invoke("finalize-contract", {
        body: { contract_id: contractId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast.success("PDF assinado reprocessado!");
    },
    onError: (e: Error) => toast.error("Erro ao reprocessar: " + e.message),
  });
}

// ─── PDF upload (pdf_upload mode) ──────────────────────────────────────────

export function useUploadContractSourcePdf() {
  return useMutation({
    mutationFn: async ({ contractId, file }: { contractId: string; file: File }) => {
      const path = `contracts/${contractId}/source.pdf`;
      const { error: uploadError } = await supabase.storage
        .from("signed-documents")
        .upload(path, file, { contentType: "application/pdf", upsert: true });
      if (uploadError) throw uploadError;

      const { data: urlData, error: urlError } = await supabase.storage
        .from("signed-documents")
        .createSignedUrl(path, 60 * 60 * 24 * 365);
      if (urlError) throw urlError;

      return urlData.signedUrl;
    },
    onError: (error) => toast.error("Erro ao enviar PDF: " + error.message),
  });
}
